import { cn } from '@lib/utils'

export type DoctorGender = 'female' | 'male'

const FEMALE_NAMES = new Set([
  'maria', 'ana', 'laura', 'sofia', 'lucia', 'elena', 'carmen', 'isabel', 'patricia',
  'andrea', 'daniela', 'gabriela', 'valentina', 'fernanda', 'alejandra', 'paula', 'sara',
  'diana', 'claudia', 'rosa', 'marta', 'monica', 'adriana', 'veronica', 'natalia',
])
const MALE_NAMES = new Set([
  'jose', 'juan', 'carlos', 'luis', 'miguel', 'jorge', 'pedro', 'manuel', 'antonio',
  'francisco', 'david', 'daniel', 'alejandro', 'roberto', 'ricardo', 'fernando', 'javier',
  'sergio', 'andres', 'diego', 'raul', 'hugo', 'mario', 'pablo', 'oscar',
])

// Best-effort gender inference from the first name (no gender field is stored
// for doctors). Falls back to the Spanish '-a' ending convention.
export function guessGender(firstName?: string): DoctorGender {
  const name = (firstName || '').trim().toLowerCase().split(/\s+/)[0]
  if (FEMALE_NAMES.has(name)) return 'female'
  if (MALE_NAMES.has(name)) return 'male'
  return name.endsWith('a') ? 'female' : 'male'
}

interface Props {
  gender: DoctorGender
  size?: number
  className?: string
}

// Animated doctor avatar — gently floats; the stethoscope uses the theme accent.
export function DoctorAvatar({ gender, size = 44, className }: Props) {
  const isFemale = gender === 'female'
  const hair = isFemale ? '#6b4226' : '#2e2017'
  const skin = '#f1c9a5'

  return (
    <div
      className={cn('animate-avatar-float shrink-0', className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg viewBox="0 0 64 64" width={size} height={size} role="img">
        <circle cx="32" cy="32" r="32" fill="rgb(var(--ml-primary) / 0.12)" />
        {/* white coat / shoulders */}
        <path d="M14 64 v-6 a18 18 0 0 1 36 0 v6 Z" fill="#ffffff" stroke="#e2e6e4" strokeWidth="1" />
        {/* collar */}
        <path d="M28 50 l4 6 4-6" fill="none" stroke="#cdd3d0" strokeWidth="1.5" />
        {/* hair back (female) */}
        {isFemale && <path d="M16 30 a16 16 0 0 1 32 0 v14 h-6 V30 a10 10 0 0 0 -20 0 v14 h-6 Z" fill={hair} />}
        {/* neck */}
        <rect x="28" y="40" width="8" height="10" rx="3" fill={skin} />
        {/* head */}
        <circle cx="32" cy="28" r="13" fill={skin} />
        {/* hair top */}
        {isFemale ? (
          <path d="M19 28 a13 13 0 0 1 26 0 q-13 -9 -26 0 Z" fill={hair} />
        ) : (
          <path d="M20 26 a12 12 0 0 1 24 0 q-12 -7 -24 0 Z" fill={hair} />
        )}
        {/* eyes */}
        <circle cx="27.5" cy="28" r="1.4" fill="#3a2a1f" />
        <circle cx="36.5" cy="28" r="1.4" fill="#3a2a1f" />
        {/* smile */}
        <path d="M28 33 q4 3 8 0" fill="none" stroke="#b5806a" strokeWidth="1.4" strokeLinecap="round" />
        {/* stethoscope */}
        <path d="M26 49 v6 a8 8 0 0 0 12 0 v-3" fill="none" stroke="rgb(var(--ml-primary))" strokeWidth="2" strokeLinecap="round" />
        <circle cx="41" cy="52" r="2.6" fill="rgb(var(--ml-primary))" />
      </svg>
    </div>
  )
}
